/**
 * Firebase Configuration - Cuartel Digital
 * Inicializa Firebase y Cloud Messaging para Push Notifications
 */

import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';

// Configuración de Firebase (reemplazar con valores del proyecto)
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "TU_API_KEY",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "TU_PROJECT.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "TU_PROJECT_ID",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "TU_PROJECT.appspot.com",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "TU_SENDER_ID",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "TU_APP_ID"
};

// Inicializar Firebase
let app = null;
let messaging = null;

try {
  app = initializeApp(firebaseConfig);
  
  // Messaging solo funciona en navegadores que lo soportan
  if ('Notification' in window && 'serviceWorker' in navigator) {
    messaging = getMessaging(app);
  } else {
    console.warn('[Firebase] Notifications no soportadas en este navegador');
  }
} catch (error) {
  console.error('[Firebase] Error al inicializar:', error);
}

/**
 * Solicitar permiso de notificaciones y obtener token FCM
 * @returns {Promise<string|null>} Token FCM o null si falla
 */
export async function requestNotificationPermission() {
  if (!messaging) {
    console.warn('[Firebase] Messaging no disponible');
    return null;
  }

  try {
    // Solicitar permiso
    const permission = await Notification.requestPermission();
    
    if (permission !== 'granted') {
      if (import.meta.env.DEV) console.log('[Firebase] Permiso de notificaciones denegado');
      return null;
    }

    // Registrar service worker si no existe
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    if (import.meta.env.DEV) console.log('[Firebase] Service Worker registrado');

    // Obtener token
    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
    const token = await getToken(messaging, {
      vapidKey: vapidKey,
      serviceWorkerRegistration: registration
    });

    if (token) {
      if (import.meta.env.DEV) console.log('[Firebase] Token FCM obtenido');
      return token;
    } else {
      console.warn('[Firebase] No se pudo obtener token');
      return null;
    }
  } catch (error) {
    console.error('[Firebase] Error al obtener permiso/token:', error);
    return null;
  }
}

/**
 * Escuchar mensajes cuando la app está en foreground
 * @param {Function} callback - Función a llamar cuando llega un mensaje
 * @returns {Function} Función para cancelar la suscripción
 */
export function onForegroundMessage(callback) {
  if (!messaging) {
    console.warn('[Firebase] Messaging no disponible');
    return () => {};
  }

  return onMessage(messaging, (payload) => {
    if (import.meta.env.DEV) console.log('[Firebase] Mensaje en foreground:', payload);
    callback(payload);
  });
}

/**
 * Verificar si las notificaciones están habilitadas
 * @returns {boolean}
 */
export function areNotificationsEnabled() {
  return 'Notification' in window && Notification.permission === 'granted';
}

/**
 * Verificar el estado del permiso de notificaciones
 * @returns {'granted' | 'denied' | 'default' | 'unsupported'}
 */
export function getNotificationPermissionStatus() {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
}

export { app, messaging };
